import gql from 'graphql-tag';
import { graphql } from 'react-relay';
import { useEffect, useState } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from 'react-apollo';

import { ConfluenceSiteAri } from '@atlassian/ari/confluence/site';

import { fg } from '@confluence/feature-gating';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { useCreateInvitationUrlCreateInvitationMutation as useCreateInvitationUrlCreateInvitationMutation$data } from './__types__/useCreateInvitationUrlCreateInvitationMutation.ts';

type UseCreateAccessUrlMutation$data = {
	admin_createAccessUrl: {
		__typename: 'AdminAccessUrlCreationResponsePayload';
		accessUrl: {
			__typename: 'AdminAccessUrl';
			id: string;
			url: string;
			resourceUrl: string;
			expirationDate: string;
			resources: Array<{
				__typename: 'AdminSite';
				id: string;
			}>;
			status: string;
		};
		success: boolean;
	};
};

type UseCreateAccessUrlMutation$variables = {
	resourceAri: string;
};

export const useCreateInvitationUrl = () => {
	const { cloudId } = useSessionData();
	const [aggCalled, setAggCalled] = useState(false);

	// CC mutation (legacy)
	const [
		createNewInviteLinkCC,
		{ data: ccData, loading: ccLoading, error: ccError, called: ccCalled },
	] = useMutation<useCreateInvitationUrlCreateInvitationMutation$data>(/** @schema cc */ gql`
		mutation useCreateInvitationUrlCreateInvitationMutation {
			createInvitationUrl {
				id
				url
			}
		}
	`);

	// AGG mutation
	const [createNewInviteLinkAGG, { data: aggData, loading: aggLoading, error: aggError }] =
		useMutationInterop<UseCreateAccessUrlMutation$data, UseCreateAccessUrlMutation$variables>(
			/** @schema agg */ graphql`
				mutation useCreateInvitationUrlMutation($resourceAri: ID!) {
					admin_createAccessUrl(resourceAri: $resourceAri) {
						__typename
						accessUrl {
							__typename
							id
							url
						}
					}
				}
			`,
		);

	const error = fg('confluence_frontend_use_agg_invite_url') ? aggError : ccError;

	useEffect(() => {
		if (error) {
			if (isUnauthorizedError(error)) {
				markErrorAsHandled(error);
			}
		}
	}, [error]);

	if (fg('confluence_frontend_use_agg_invite_url')) {
		return {
			createNewInviteLink: async () => {
				setAggCalled(true);
				const siteAri = cloudId
					? ConfluenceSiteAri.create({
							siteId: cloudId,
						}).toString()
					: '';
				return createNewInviteLinkAGG({
					variables: {
						resourceAri: siteAri,
					},
				});
			},
			newInviteLink: aggData?.admin_createAccessUrl?.accessUrl?.url,
			loading: aggLoading,
			error: aggError,
			called: aggCalled,
		};
	}
	return {
		createNewInviteLink: createNewInviteLinkCC,
		newInviteLink: ccData?.createInvitationUrl?.url,
		loading: ccLoading,
		error: ccError,
		called: ccCalled,
	};
};
